import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Toolbar,
  Container,
  Grid,
  Paper,
  Stack,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '../organisms/AppBar';
import Drawer from '../organisms/Drawer';
import Copyright from '../molecules/Copyright';
import Information from '../molecules/Information';
import Usage from '../organisms/Usage';
import SetupTransfer from '../molecules/SetupTransfer';
import Log from '../../lib/log';
import { v2api } from '../../api/axios';
import { useSelector } from '../../store/store';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(timezone);
dayjs.extend(utc);

// ログの初期化
const log = new Log('Dashboard');

// お知らせ情報タイプ
export interface InfoType {
  startDate: string;
  information: string;
}

// 利用状況タイプ
interface RecordType {
  target_month: string;
  incoming_count: number;
  recorded_count: number;
}
export interface UsageType {
  records: RecordType[];
  response_change_count: number;
  amount: number;
}

function DashboardContent() {
  log.putLog('info', 'Dashboard started.');

  // ストアからユーザー情報を取得
  // const user = useSelector((state) => state.loginUser.user);
  const [user] = useState(useSelector((state) => state.loginUser.user));

  useEffect(() => {
    (async () => {
      try {
        // kintoneからお知らせを取得する
        const postData = {
          userId: user.id,
          token: user.access_token,
        };
        const res = await v2api.post('/get-info', postData);
        if (res.data.statusCode === 200) {
          // 取得成功
          const records = JSON.parse(res.data.body).records;
          const infos: InfoType[] = records.map((record: any) => {
            const startDate = dayjs(record.startDate.value)
              .tz('Asia/Tokyo')
              .format('YYYY-MM-DD HH:mm');
            const info = {
              startDate,
              information:
                record.information.value.replace(/\n/g, '<br>') || '',
            };
            return info;
          });
          setInfos(infos);
        }
      } catch (err: any) {
        log.putLog('error', err.message ? err.message : err);
      }
      (async () => {
        try {
          // 利用状況を取得する
          const postData = {
            userId: user.id,
            token: user.access_token,
          };
          const res = await v2api.post('/get-usage', postData);
          if (res.data.statusCode === 200) {
            // 取得成功
            const body = JSON.parse(res.data.body);
            setUsage(body);
          }
        } catch (err: any) {
          log.putLog('error', err.message ? err.message : err);
        }
      })();
    })();
  }, [user]);

  // お知らせ情報
  const [infos, setInfos] = useState<InfoType[]>();
  // 利用状況
  const [usage, setUsage] = useState<UsageType>();

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar title='ダッシュボード' />
      <Drawer />
      <Box
        component='main'
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={6}>
              <Stack>
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'auto',
                  }}
                >
                  <Typography
                    variant='h6'
                    gutterBottom
                    component='div'
                    color={'gray'}
                  >
                    お知らせ
                  </Typography>
                  {infos?.length === 0 ? (
                    <div>現在、お知らせはありません。</div>
                  ) : (
                    <Information infos={infos} />
                  )}
                </Paper>
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'auto',
                    mt: 3,
                  }}
                >
                  <Typography
                    variant='h6'
                    gutterBottom
                    component='div'
                    color={'gray'}
                  >
                    転送設定ガイド
                  </Typography>
                  <SetupTransfer
                    id={user.id}
                    token={user.access_token}
                    serviceNumber={user.service_number}
                  />
                </Paper>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'auto',
                }}
              >
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  color={'gray'}
                >
                  ご利用状況
                </Typography>
                <Usage usage={usage} />
              </Paper>
            </Grid>
          </Grid>
          <Copyright sx={{ pt: 4 }} />
        </Container>
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  return <DashboardContent />;
}
